import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Documents from "@/pages/Documents";
import Filings from "@/pages/Filings";
import Safety from "@/pages/Safety";
import Drivers from "@/pages/Drivers";
import { ArrowLeft, Building2, Phone, Hash, KeyRound, Copy, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

// Generate a memorable but strong 12-char password
function genPassword() {
  const words = ["Truck", "Fleet", "Road", "Cargo", "Haul", "Diesel", "Route", "Miles", "Axle", "Ranger"];
  const w = words[Math.floor(Math.random() * words.length)];
  const num = Math.floor(1000 + Math.random() * 9000);
  const sym = ["!", "@", "#", "$", "&"][Math.floor(Math.random() * 5)];
  return `${w}${num}${sym}`;
}

export default function AdminClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("filings");
  const [editing, setEditing] = useState(false);
  const [resettingPwd, setResettingPwd] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/clients/${id}`);
    setData(data);
  };
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="hs-overline">LOADING…</div>;

  const c = data.client;
  return (
    <div className="space-y-6" data-testid="admin-client-detail">
      <Link to="/admin/clients" className="hs-btn-ghost inline-flex items-center gap-2 text-xs" data-testid="back-to-clients">
        <ArrowLeft size={14}/> ALL CLIENTS
      </Link>

      <div className="hs-card p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="hs-overline">CLIENT FILE</div>
            <h1 className="font-display font-black text-3xl uppercase tracking-tight mt-1">{c.name}</h1>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-500 font-mono">
              {c.company_name && <span className="inline-flex items-center gap-1"><Building2 size={14}/> {c.company_name}</span>}
              {c.phone && <span className="inline-flex items-center gap-1"><Phone size={14}/> {c.phone}</span>}
              {c.dot_number && <span className="inline-flex items-center gap-1"><Hash size={14}/> DOT {c.dot_number}</span>}
              {c.mc_number && <span className="inline-flex items-center gap-1"><Hash size={14}/> MC {c.mc_number}</span>}
            </div>
            <div className="mt-3 text-xs text-slate-500">{c.email}</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button className="hs-btn-secondary" onClick={() => setEditing(true)} data-testid="edit-client-button">EDIT PROFILE</button>
            <button className="hs-btn-ghost inline-flex items-center gap-1 text-xs" onClick={() => setResettingPwd(true)} data-testid="reset-password-button">
              <KeyRound size={12}/> SET / RESET PASSWORD
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <TabButton active={tab === "filings"} onClick={() => setTab("filings")} testid="tab-filings">Filings ({data.filings.length})</TabButton>
        <TabButton active={tab === "drivers"} onClick={() => setTab("drivers")} testid="tab-drivers">Drivers ({(data.drivers || []).length})</TabButton>
        <TabButton active={tab === "documents"} onClick={() => setTab("documents")} testid="tab-documents">Documents ({data.documents.length})</TabButton>
        <TabButton active={tab === "safety"} onClick={() => setTab("safety")} testid="tab-safety">Safety</TabButton>
      </div>

      {tab === "filings" && <Filings adminClientId={id} />}
      {tab === "drivers" && <Drivers adminClientId={id} />}
      {tab === "documents" && <Documents adminClientId={id} />}
      {tab === "safety" && <Safety adminClientId={id} />}

      {editing && <EditModal client={c} onClose={() => setEditing(false)} onSaved={async () => { setEditing(false); await load(); toast.success("Updated"); }} />}
      {resettingPwd && <ResetPasswordModal client={c} onClose={() => setResettingPwd(false)} />}
    </div>
  );
}

function ResetPasswordModal({ client, onClose }) {
  const [pwd, setPwd] = useState(genPassword());
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const copyCreds = async () => {
    const text = `Portal: ${window.location.origin}\nEmail: ${client.email}\nPassword: ${pwd}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Credentials copied to clipboard");
    } catch {
      toast.error("Copy failed — select manually");
    }
  };

  const save = async () => {
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/clients/${client.id}/set-password`, { password: pwd });
      setSaved(true);
      toast.success("Password saved. Share the credentials with the client.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set password");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="hs-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="reset-password-modal">
        <div className="hs-overline text-blue-700">MANAGED CREDENTIALS</div>
        <h3 className="font-display font-bold text-2xl mt-1">Set / Reset Password</h3>
        <p className="text-xs text-slate-500 mt-2">
          Managed-portal design: your compliance team owns the login. Set a password here and hand it to the client.
          They cannot change it themselves.
        </p>

        <div className="mt-5 space-y-3">
          <div>
            <label className="hs-overline">Client Email (login)</label>
            <input readOnly value={client.email} className="hs-input mt-2 font-mono bg-slate-50" data-testid="reset-email" />
          </div>
          <div>
            <label className="hs-overline flex items-center justify-between">
              <span>New Password</span>
              <button
                type="button"
                onClick={() => { setPwd(genPassword()); setSaved(false); }}
                className="hs-btn-ghost inline-flex items-center gap-1 text-[10px]"
                data-testid="reset-generate-btn"
              >
                <RefreshCcw size={10}/> GENERATE
              </button>
            </label>
            <input
              type="text"
              value={pwd}
              onChange={(e) => { setPwd(e.target.value); setSaved(false); }}
              className="hs-input mt-2 font-mono text-base"
              data-testid="reset-password-input"
              minLength={8}
              maxLength={128}
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              name="managed_client_new_password"
              spellCheck={false}
            />
            <div className="mt-2 bg-blue-50 border border-blue-300 px-3 py-2 flex items-center gap-2" data-testid="reset-password-preview">
              <span className="hs-overline text-[10px] text-blue-700">PASSWORD YOU&rsquo;LL SEND →</span>
              <span className="font-mono text-lg font-bold text-blue-900 select-all break-all flex-1">{pwd || "—"}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-2">Minimum 8 characters. Share with the client verbally or via secure channel.</div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap justify-between gap-2">
          <button onClick={copyCreds} className="hs-btn-secondary inline-flex items-center gap-2" data-testid="reset-copy-creds">
            <Copy size={14}/> COPY CREDENTIALS
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="hs-btn-secondary" data-testid="reset-close">
              {saved ? "DONE" : "CANCEL"}
            </button>
            <button onClick={save} disabled={busy} className="hs-btn-primary" data-testid="reset-save-btn">
              {busy ? "SAVING…" : saved ? "SAVED ✓" : "SAVE PASSWORD"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, children, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-sm border-b-2 transition-colors ${active ? "border-blue-700 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
      {children}
    </button>
  );
}

function EditModal({ client, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: client.name || "", company_name: client.company_name || "",
    phone: client.phone || "", dot_number: client.dot_number || "", mc_number: client.mc_number || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/clients/${client.id}`, form);
      onSaved();
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="hs-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="hs-overline">EDIT</div>
        <h3 className="font-display font-bold text-2xl">{client.email}</h3>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          {Object.entries(form).map(([k, v]) => (
            <div key={k}>
              <label className="hs-overline">{k.replace("_", " ")}</label>
              <input className="hs-input mt-2" value={v} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`edit-${k}`} />
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="hs-btn-secondary">CANCEL</button>
          <button onClick={save} disabled={busy} className="hs-btn-primary" data-testid="edit-client-save">{busy ? "SAVING…" : "SAVE"}</button>
        </div>
      </div>
    </div>
  );
}
